import * as React from "react";
import styled from "styled-components";

import { createEntitiesConsumer } from "../../lib/entities";
import { Image } from "../../entities";
import { Post } from "../entities";
import { colors, media, truncateLines } from "../../common/utils/style";
import Translate from "../../common/components/Translate";
import ImageComponent from "../../common/components/Image";
import Icon from "../../common/components/Icon";

interface Props {
  post: Post;
}

interface PlaceholderProps {
  linkPath: string;
}

const ImageEntitiesConsumer = createEntitiesConsumer<{ image: Image }>({ image: "images" });

const PostCardSimple: React.SFC<Props> = props => {
  const { post } = props;
  return (
    <Base href={post.post_path}>
      <CoverImageWrapper>
        <ImageEntitiesConsumer query={{ image: post.image }}>
          {({ image }) =>
            image && (
              <CoverImage
                image={image}
                alt={post.title}
                size="330x190#"
                background
                /* Stop lazy for faster First meaningful paint used of FeaturedPostCard */
                lazy={false}
              />
            )
          }
        </ImageEntitiesConsumer>
      </CoverImageWrapper>
      <Title>{post.title}</Title>
    </Base>
  );
};

const PostCardSimplePlaceholder: React.SFC<PlaceholderProps> = props => {
  const { linkPath } = props;
  return (
    <Base href={linkPath}>
      <ImagePlaceholder>
        <PlusIcon name="wt-icon-plus-o-1" />
        <PlaceholderInnerText>
          <Translate id="company.posts.new_posts" />
        </PlaceholderInnerText>
      </ImagePlaceholder>
      <TextPlaceholder />
      <TextPlaceholder />
    </Base>
  );
};

const Base = styled.a`
  display: block;
  color: inherit;
  &:hover {
    text-decoration: none;
  }
`;

const CoverImageWrapper = styled.div`
  height: 190px;
  border-radius: 4px;
  margin-bottom: 12px;
  background-color: ${colors.lightergray2};
  overflow: hidden;
  @media ${media.large} {
    height: 130px;
    margin-bottom: 15px;
  }
`;

const CoverImage = styled(ImageComponent)`
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: 0.6s ease-out;

  ${Base}:hover & {
    transform: scale(1.15);
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.8);
  transition: color 0.2s ease-in-out;
  ${truncateLines(2, 16, 1.5)};

  @media ${media.large} {
    font-size: 14px;
    ${truncateLines(2, 14, 1.5)};
  }

  ${Base}:hover & {
    color: #21bddb; /* blue400 */
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
  border-radius: 4px;
  height: 190px;
  @media ${media.large} {
    height: 130px;
  }
`;

const PlusIcon = styled(Icon)`
  color: rgba(0, 0, 0, 0.4);
  font-size: 20px;
  margin-top: -4px;
  margin-right: 8px;
`;

const PlaceholderInnerText = styled.p`
  font-size: 17px;
  color: rgba(0, 0, 0, 0.4);
`;

const TextPlaceholder = styled.span`
  display: block;
  content: "";
  background-color: #fafafa;
  height: 12px;
  border-radius: 6px;

  ${Base} > & {
    margin-top: 20px;

    &:last-of-type {
      margin-top: 8px;
      width: 160px;
    }
  }
`;

export default PostCardSimple;
export { PostCardSimplePlaceholder };



// WEBPACK FOOTER //
// ./frontend/src/posts/components/PostCardSimple.tsx